/**
 * MAPS surface feature module for MJExplorer.
 *
 * A thin host around the shared MAPS UI (packages/MAPSUI — the same source the
 * standalone app serves). Declares only the BaseDashboard host that the
 * Explorer shell instantiates via the 'MAPSAppSurface' driver class, and stands
 * up a dedicated apollo-angular client pointed at /maps-graphql. The Explorer
 * implementation of the MAPS_HOST_ADAPTER seam is provided by
 * MapsSurfaceComponent, not here: this module is imported eagerly into
 * AppModule, so its providers are root-injector singletons shared app-wide.
 *
 * MJExplorer itself does not use apollo-angular (MJ has its own data
 * provider), so this default client is exclusively MAPS's. An auth link
 * attaches the signed-in MJ user's bearer (MJAuthBase.getIdToken — the same
 * token MJExplorer uses for its own /graphql calls) to EVERY operation; the
 * server resolves the MAPS session from that bearer. No cookie, no bridge
 * handshake.
 */
import { NgModule, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { MJAuthBase } from '@memberjunction/ng-auth-services';
import { MapsUiModule } from 'maps_ui';
import { environment } from '../../environments/environment';

import { MapsSurfaceComponent, LoadMapsSurface } from './maps-surface.component';

/**
 * Derive /maps-graphql from MJExplorer's own GraphQL endpoint (same host/port).
 * GRAPHQL_URI is the bare API origin in this app's environments (MJ mounts its
 * GraphQL at the root — e.g. 'http://localhost:4000/'), but tolerate a
 * '/graphql'-suffixed value too: strip either shape, then append the MAPS mount.
 */
function mapsGraphqlUri(): string {
    const base = (environment as { GRAPHQL_URI?: string }).GRAPHQL_URI || 'http://localhost:4000/';
    return `${base.replace(/\/graphql\/?$/, '').replace(/\/+$/, '')}/maps-graphql`;
}

@NgModule({
    declarations: [MapsSurfaceComponent],
    imports: [CommonModule, MapsUiModule],
    // The Apollo client stays at the root on purpose: maps_ui's services (SearchService,
    // MapsSessionService, …) are `providedIn: 'root'` and inject `Apollo`, so it must be resolvable
    // there; it holds no per-surface state (network-only fetch policy, bearer read per request).
    providers: [
        provideApollo(() => {
            const httpLink = inject(HttpLink);
            const auth = inject(MJAuthBase);
            const authLink = setContext(async () => {
                const token = await auth.getIdToken();
                return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            });
            return {
                link: authLink.concat(
                    httpLink.create({ uri: mapsGraphqlUri() })
                ),
                cache: new InMemoryCache(),
                defaultOptions: {
                    watchQuery: { fetchPolicy: 'network-only' },
                    query: { fetchPolicy: 'network-only' },
                },
            };
        }),
    ],
})
export class MapsSurfaceModule {
    constructor() {
        LoadMapsSurface();
    }
}
